import { TFunction } from "i18next";

import { convertValueToLocale } from "helpers/convertValueToLocale";
import { v4 as uuid } from "uuid";

import { CALCULATOR_SCENARIO_VARIANT } from "constants/calculator";

import styles from "./Conclusions.module.scss";

export const getIrr = ({
  conclusionsPro,
  userScenarioResult,
  activePosition,
}) => {
  let irr = 0;
  if (userScenarioResult === CALCULATOR_SCENARIO_VARIANT.POSITIVE) {
    irr = conclusionsPro?.cashFlowConclusions?.irrPositive?.[activePosition];
  } else if (userScenarioResult === CALCULATOR_SCENARIO_VARIANT.NEUTRAL) {
    irr = conclusionsPro?.cashFlowConclusions?.irrNeutral?.[activePosition];
  } else if (userScenarioResult === CALCULATOR_SCENARIO_VARIANT.NEGATIVE) {
    irr = conclusionsPro?.cashFlowConclusions?.irrNegative?.[activePosition];
  }
  return irr;
};

export const getRoi = ({
  conclusionsPro,
  userScenarioResult,
  activePosition,
}) => {
  let roi = 0;
  if (userScenarioResult === CALCULATOR_SCENARIO_VARIANT.POSITIVE) {
    roi = conclusionsPro?.cashFlowConclusions?.roiPositive?.[activePosition];
  } else if (userScenarioResult === CALCULATOR_SCENARIO_VARIANT.NEUTRAL) {
    roi = conclusionsPro?.cashFlowConclusions?.roiNeutral?.[activePosition];
  } else if (userScenarioResult === CALCULATOR_SCENARIO_VARIANT.NEGATIVE) {
    roi = conclusionsPro?.cashFlowConclusions?.roiNegative?.[activePosition];
  }
  return roi;
};

export const getIrrConclusion = (irr) => {
  if (irr >= 10) {
    return {
      title: `Dein Eigenkapitalrendite (IRR-Methode) von <span class="${
        styles.chartTitlePositive
      }">${convertValueToLocale(irr, 2, "%")}</span>
              ist ein exzellentes Ergebnis! Diese
              Investition wirft deutlich schneller Gewinn ab als die meisten
              Alternativen`,
      description: `Ein hoher Eigenkapitalrendite (IRR-Methode) bedeutet, dass das Verhältnis zwischen
              Kaufpreis, Mieteinnahmen und Wertsteigerungspotenzial optimal ist.
              Solche Objekte gelten als erstklassige Investments, da sie eine hohe
              Rendite und einen stabilen Cashflow versprechen. Professionelle
              Investoren sehen eine solche Kennzahl als Zeichen für ein sehr
              lohnenswertes Geschäft.`,
    };
  }

  if (irr >= 7) {
    return {
      title: `Dein Eigenkapitalrendite (IRR-Methode) von <span class="${
        styles.chartTitlePositive
      }">${convertValueToLocale(irr, 2, "%")}</span>
              ist ein gutes Ergebnis. Dein Geld
              arbeitet effizient und wächst schneller als der Markt`,
      description: `Ein solcher Eigenkapitalrendite (IRR-Methode) deutet auf eine sehr effiziente Investition
              hin. Kaufpreis und erwartete Erträge stehen in einer guten Balance, was
              ein überdurchschnittliches Kapitalwachstum sichert. Dies ist ein
              Anzeichen für ein qualitativ hochwertiges Objekt in einer
              zukunftsträchtigen Lage.`,
    };
  }

  if (irr >= 5) {
    return {
      title: `Dein Eigenkapitalrendite (IRR-Methode) von <span class="${
        styles.chartTitleNeutral
      }">${convertValueToLocale(irr, 2, "%")}</span>
              ist ein solides Ergebnis. Dein Geld
              wächst zuverlässig, wie es von einer guten Investition erwartet wird.`,
      description: `Dies ist die Kennzahl für eine solide, marktübliche
              Investition. Die Rendite deckt die Risiken ab, und es wird ein stabiles
              W ertwachstum erwartet. Für professionelle Investoren k ö nnte ein
              solches Objekt jedoch nicht optimal sein. Es ist wichtig, auf weitere
              Parameter wie Lage und Ausstattungsqualität zu achten, um die
              zukünftige Nachfrage zu sichern.`,
    };
  }

  return {
    title: `Dein Eigenkapitalrendite (IRR-Methode) von <span class="${
      styles.chartTitleNegative
    }">${convertValueToLocale(irr, 2, "%")}</span>
            ist ein schwaches Ergebnis. Dein
            Kapitalwachstum übersteigt kaum die Inflation, die Investition ist
            ineffizient.`,
    description: `Ein niedriger Eigenkapitalrendite (IRR-Methode) signalisiert ein hohes Risiko. Entweder
            ist der Kaufpreis zu hoch oder die potenziellen Mieteinnahmen sind zu
            gering. Eine solche Investition ist stark von einem zukünftigen,
            unvorhersehbaren Marktwachstum abhängig. Professionelle Investoren
            meiden Objekte mit einer solchen Kennzahl in der Regel aufgrund des
            ungünstigen Risiko - Rendite -V erhältnisses.`,
  };
};

export const getIrrChartsData = ({
  userScenarioResult,
  activePosition,
  conclusionsPro,
  proCalculation,
}: {
  userScenarioResult: string;
  activePosition: 10 | 20 | 30;
  conclusionsPro: any;
  proCalculation: any;
}) => {
  const { cashFlowConclusions } = conclusionsPro || {};

  // if (!cashFlowConclusions?.irr?.[activePosition]) {
  //   return {
  //     irr: 0,
  //     description: "",
  //     chartData: [],
  //   };
  // }

  const sonderAfa = proCalculation?.baseCalculation?.isSonderAfaEnabled;
  const degressiveAfa = proCalculation?.baseCalculation?.isDegressiveAfaEnabled;
  const eth40 = proCalculation?.baseCalculation?.isKfw297Limit100Enabled;
  const eth40Qng = proCalculation?.baseCalculation?.isKfw297Limit150Enabled;

  if (userScenarioResult === CALCULATOR_SCENARIO_VARIANT.POSITIVE) {
    const derArticle = sonderAfa || degressiveAfa || eth40Qng;

    const irrPositive =
      Math.round(
        (cashFlowConclusions.irrPositive?.[activePosition] || 0) * 100
      ) / 100;
    const irrNegative =
      Math.round(
        (cashFlowConclusions.irrNegative?.[activePosition] || 0) * 100
      ) / 100;
    const irrDifferenceNegative = irrPositive - irrNegative;

    return {
      price: convertValueToLocale(irrPositive, 2, "%"),
      caption: `${convertValueToLocale(
        irrNegative,
        2,
        "%"
      )} ohne Fördermaßnahmen`,
      description: `Dein zusätzlicher Vorteil durch Förderung +${convertValueToLocale(
        irrDifferenceNegative,
        2,
        "%"
      )}`,
      details: `Perfekt! Max. mögl. Vorteil`,
      chartData: [
        {
          chartItem: convertValueToLocale(irrPositive, 2, "%"),
          value: irrPositive,
          valueColor: "#469c49",
        },
        {
          chartItem: convertValueToLocale(irrNegative, 2, "%"),
          value: irrNegative,
          valueColor: "#DADDEA",
        },
      ],
      info: {
        section1: `Dank 
                  ${derArticle ? `der ` : ""}
                  ${sonderAfa ? `Sonder ` : ""}
                  ${sonderAfa ? `und ` : ""}
                  ${degressiveAfa ? `degressiven ` : ""}
                  ${degressiveAfa || sonderAfa ? `AfA` : ""} 
                  ${degressiveAfa || sonderAfa ? `und ` : ""}
                  ${
                    eth40Qng
                      ? `des KfW-geförderten Kredites nach 298 für enegrieeffizientes (EH40) und nachhaltiges (QNG) Bauen`
                      : ""
                  } 
                  beträgt die Eigenkapitalrendite nach IRR-Methode in den ersten
                  ${activePosition}
                  Jahren den höchstmöglichen Wert von 
                  ${convertValueToLocale(irrPositive, 2, "%")}
                  im Vergleich zu 
                  ${convertValueToLocale(irrNegative, 2, "%")}
                  bei derselben Wohnung ohne diese Fördermaßnahmen.`,
        section2: null,
      },
    };
  }

  if (userScenarioResult === "neutral") {
    const derArticle = sonderAfa || degressiveAfa || eth40Qng || eth40;

    const irrPositive =
      Math.round(
        (cashFlowConclusions.irrPositive?.[activePosition] || 0) * 100
      ) / 100;
    const irrNeutral =
      Math.round(
        (cashFlowConclusions.irrNeutral?.[activePosition] || 0) * 100
      ) / 100;
    const irrNegative =
      Math.round(
        (cashFlowConclusions.irrNegative?.[activePosition] || 0) * 100
      ) / 100;
    const irrDifferenceNegative = irrNeutral - irrNegative;
    const irrDifferencePositive = irrPositive - irrNeutral;

    return {
      price: convertValueToLocale(irrNeutral, 2, "%"),
      caption: `${convertValueToLocale(
        irrNegative,
        2,
        "%"
      )} ohne Fördermaßnahmen`,
      description: `Dein zusätzlicher Vorteil durch Förderung +${convertValueToLocale(
        irrDifferenceNegative,
        2,
        "%"
      )}`,
      details: `${convertValueToLocale(
        irrDifferencePositive,
        2,
        "%"
      )} weniger als max. mögl. Vorteil`,
      chartData: [
        {
          chartItem: convertValueToLocale(irrNeutral, 2, "%"),
          value: irrNeutral,
          valueColor: "#ffbe42",
        },
        {
          chartItem: convertValueToLocale(irrNegative, 2, "%"),
          value: irrNegative,
          valueColor: "#DADDEA",
        },
      ],
      info: {
        section1: `Dank 
                  ${derArticle ? `der ` : ""}
                  ${sonderAfa ? `Sonder ` : ""}
                  ${sonderAfa ? `und ` : ""}
                  ${degressiveAfa ? `degressiven ` : ""}
                  ${degressiveAfa || sonderAfa ? `und ` : ""}
                  ${
                    (degressiveAfa || sonderAfa) && eth40
                      ? `des KfW-geförderten Kredites nach 298 für enegrieeffizientes (EH40)`
                      : ""
                  }
                  ${
                    (degressiveAfa || sonderAfa) && eth40Qng
                      ? `des KfW-geförderten Kredites nach 298 für enegrieeffizientes (EH40) und nachhaltiges (QNG) Bauen`
                      : ""
                  }
                  beträgt die Eigenkapitalrendite nach IRR-Methode in den ersten 
                  ${activePosition} 
                  Jahren den höchstmöglichen Wert von 
                  ${convertValueToLocale(irrNeutral, 2, "%")}
                  im Vergleich zu 
                  ${convertValueToLocale(irrNegative, 2, "%")}
                  bei derselben Wohnung ohne diese Fördermaßnahmen.`,
        section2: `Die höchstmögliche Eigenkapitalrendite nach IRR-Methode bei derselben Wohnung mit 
                  ${!sonderAfa ? `Sonder` : ""}
                  ${!degressiveAfa && !sonderAfa ? `und` : ""}
                  ${!degressiveAfa ? `degressiven` : ""}
                  ${!degressiveAfa || !sonderAfa ? `AfA` : ""}
                  ${(!degressiveAfa || !sonderAfa) && !eth40Qng ? `und` : ""}
                  ${
                    !eth40Qng
                      ? `dem KfW-geförderten Kredit nach 298 für enegrieeffizienten (EH40) und nachhaltigen (QNG) Bauen`
                      : ""
                  }
                  würde
                  ${convertValueToLocale(irrPositive, 2, "%")} betragen.`,
      },
    };
  }

  const irrPositive =
    Math.round((cashFlowConclusions.irrPositive?.[activePosition] || 0) * 100) /
    100;
  const irrNegative =
    Math.round((cashFlowConclusions.irrNegative?.[activePosition] || 0) * 100) /
    100;
  const irrDifferenceNegative = irrPositive - irrNegative;

  return {
    price: convertValueToLocale(irrNegative, 2, "%"),
    caption: `${convertValueToLocale(irrPositive, 2, "%")} mit Fördermaßnahmen`,
    description: `${convertValueToLocale(
      irrDifferenceNegative,
      2,
      "%"
    )} weniger als max. mögl. Vorteil`,
    details: null,
    chartData: [
      {
        chartItem: convertValueToLocale(irrNegative, 2, "%"),
        value: irrNegative,
        valueColor: "#f85608",
      },
      {
        chartItem: convertValueToLocale(irrPositive, 2, "%"),
        value: irrPositive,
        valueColor: "#DADDEA",
      },
    ],
    info: {
      section1: `Die Eigenkapitalrendite nach IRR-Methode beträgt in den ersten 
                ${activePosition} 
                Jahren 
                ${convertValueToLocale(irrNegative, 2, "%")} 
                im Vergleich zu
                ${convertValueToLocale(irrPositive, 2, "%")} 
                bei derselben Wohnung mit der Sonder
                ${!degressiveAfa ? `und degressiven` : ""}
                AfA und des KfW-geförderten Kredites nach 298 für enegrieeffizientes (EH40) und nachhaltiges (QNG) Bauen.`,
      section2: null,
    },
  };
};

export const getRoiChartsData = ({
  userScenarioResult,
  activePosition,
  conclusionsPro,
  proCalculation,
}: {
  userScenarioResult: string;
  activePosition: 10 | 20 | 30;
  conclusionsPro: any;
  proCalculation: any;
}) => {
  const { cashFlowConclusions } = conclusionsPro || {};

  const sonderAfa = proCalculation?.baseCalculation?.isSonderAfaEnabled;
  const degressiveAfa = proCalculation?.baseCalculation?.isDegressiveAfaEnabled;
  const eth40 = proCalculation?.baseCalculation?.isKfw297Limit100Enabled;
  const eth40Qng = proCalculation?.baseCalculation?.isKfw297Limit150Enabled;

  if (userScenarioResult === CALCULATOR_SCENARIO_VARIANT.POSITIVE) {
    const derArticle = sonderAfa || degressiveAfa || eth40Qng;

    const roiPositive =
      Math.round(
        (cashFlowConclusions.roiPositive?.[activePosition] || 0) * 100
      ) / 100;
    const roiNegative =
      Math.round(
        (cashFlowConclusions.roiNegative?.[activePosition] || 0) * 100
      ) / 100;
    const roiDifferenceNegative = roiPositive - roiNegative;

    return {
      price: convertValueToLocale(roiPositive, 2, "%"),
      caption: `${convertValueToLocale(
        roiNegative,
        2,
        "%"
      )} ohne Fördermaßnahmen`,
      description: `Dein zusätzlicher Vorteil durch Förderung +${convertValueToLocale(
        roiDifferenceNegative,
        2,
        "%"
      )}`,
      details: `Perfekt! Max. mögl. Vorteil`,
      chartData: [
        {
          chartItem: convertValueToLocale(roiPositive, 2, "%"),
          value: roiPositive,
          valueColor: "#469c49",
        },
        {
          chartItem: convertValueToLocale(roiNegative, 2, "%"),
          value: roiNegative,
          valueColor: "#DADDEA",
        },
      ],
      info: {
        section1: `Dank 
                  ${derArticle ? `der ` : ""}
                  ${sonderAfa ? `Sonder ` : ""}
                  ${sonderAfa ? `und ` : ""}
                  ${degressiveAfa ? `degressiven ` : ""}
                  ${degressiveAfa || sonderAfa ? `AfA` : ""} 
                  ${degressiveAfa || sonderAfa ? `und ` : ""}
                  ${
                    eth40Qng
                      ? `des KfW-geförderten Kredites nach 298 für enegrieeffizientes (EH40) und nachhaltiges (QNG) Bauen`
                      : ""
                  } 
                  beträgt das ROI in den ersten 
                  ${activePosition}
                  Jahren den höchstmöglichen Wert von 
                  ${convertValueToLocale(roiPositive, 2, "%")}
                  im Vergleich zu 
                  ${convertValueToLocale(roiNegative, 2, "%")}
                  bei derselben Wohnung ohne diese Fördermaßnahmen.`,
        section2: null,
      },
    };
  }

  if (userScenarioResult === "neutral") {
    const derArticle = sonderAfa || degressiveAfa || eth40Qng || eth40;

    const roiPositive =
      Math.round(
        (cashFlowConclusions.roiPositive?.[activePosition] || 0) * 100
      ) / 100;
    const roiNeutral =
      Math.round(
        (cashFlowConclusions.roiNeutral?.[activePosition] || 0) * 100
      ) / 100;
    const roiNegative =
      Math.round(
        (cashFlowConclusions.roiNegative?.[activePosition] || 0) * 100
      ) / 100;
    const roiDifferenceNegative = roiNeutral - roiNegative;
    const roiDifferencePositive = roiPositive - roiNeutral;

    return {
      price: convertValueToLocale(roiNeutral, 2, "%"),
      caption: `${convertValueToLocale(
        roiNegative,
        2,
        "%"
      )} ohne Fördermaßnahmen`,
      description: `Dein zusätzlicher Vorteil durch Förderung +${convertValueToLocale(
        roiDifferenceNegative,
        2,
        "%"
      )}`,
      details: `${convertValueToLocale(
        roiDifferencePositive,
        2,
        "%"
      )} weniger als max. mögl. Vorteil`,
      chartData: [
        {
          chartItem: convertValueToLocale(roiNeutral, 2, "%"),
          value: roiNeutral,
          valueColor: "#ffbe42",
        },
        {
          chartItem: convertValueToLocale(roiNegative, 2, "%"),
          value: roiNegative,
          valueColor: "#DADDEA",
        },
      ],
      info: {
        section1: `Dank 
                  ${derArticle ? `der ` : ""}
                  ${sonderAfa ? `Sonder ` : ""}
                  ${sonderAfa ? `und ` : ""}
                  ${degressiveAfa ? `degressiven ` : ""}
                  ${degressiveAfa || sonderAfa ? `und ` : ""}
                  ${
                    (degressiveAfa || sonderAfa) && eth40
                      ? `des KfW-geförderten Kredites nach 298 für enegrieeffizientes (EH40)`
                      : ""
                  }
                  ${
                    (degressiveAfa || sonderAfa) && eth40Qng
                      ? `des KfW-geförderten Kredites nach 298 für enegrieeffizientes (EH40) und nachhaltiges (QNG) Bauen`
                      : ""
                  }
                  beträgt das ROI in den ersten
                  ${activePosition} 
                  Jahren 
                  ${convertValueToLocale(roiNeutral, 2, "%")}
                  im Vergleich zu 
                  ${convertValueToLocale(roiNegative, 2, "%")}
                  bei derselben Wohnung ohne diese Fördermaßnahmen.`,
        section2: `Das höchstmögliche ROI bei derselben Wohnung mit
                  ${!sonderAfa ? `Sonder` : ""}
                  ${!degressiveAfa && !sonderAfa ? `und` : ""}
                  ${!degressiveAfa ? `degressiven` : ""}
                  ${!degressiveAfa || !sonderAfa ? `AfA` : ""}
                  ${(!degressiveAfa || !sonderAfa) && !eth40Qng ? `und` : ""}
                  ${
                    !eth40Qng
                      ? `dem KfW-geförderten Kredit nach 298 für enegrieeffizienten (EH40) und nachhaltigen (QNG) Bauen`
                      : ""
                  }
                  würde
                  ${convertValueToLocale(roiPositive, 2, "%")} betragen.`,
      },
    };
  }

  const roiPositive =
    Math.round((cashFlowConclusions.roiPositive?.[activePosition] || 0) * 100) /
    100;
  const roiNegative =
    Math.round((cashFlowConclusions.roiNegative?.[activePosition] || 0) * 100) /
    100;
  const roiDifferenceNegative = roiPositive - roiNegative;

  return {
    price: convertValueToLocale(roiNegative, 2, "%"),
    caption: `${convertValueToLocale(roiPositive, 2, "%")} mit Fördermaßnahmen`,
    description: `${convertValueToLocale(
      roiDifferenceNegative,
      2,
      "%"
    )} weniger als max. mögl. Vorteil`,
    details: null,
    chartData: [
      {
        chartItem: convertValueToLocale(roiNegative, 2, "%"),
        value: roiNegative,
        valueColor: "#f85608",
      },
      {
        chartItem: convertValueToLocale(roiPositive, 2, "%"),
        value: roiPositive,
        valueColor: "#DADDEA",
      },
    ],
    info: {
      section1: `Das ROI beträgt in den ersten 
                ${activePosition} 
                Jahren 
                ${convertValueToLocale(roiNegative, 2, "%")} 
                im Vergleich zu
                ${convertValueToLocale(roiPositive, 2, "%")} 
                bei derselben Wohnung mit der Sonder
                ${!degressiveAfa ? `und degressiven` : ""}
                AfA und des KfW-geförderten Kredites nach 298 für enegrieeffizientes (EH40) und nachhaltiges (QNG) Bauen.`,
      section2: null,
    },
  };
};

export const getInvestorCashFlowConclusion = ({
  activePosition,
  conclusionsPro,
  userScenarioResult,
  proCalculation,
}: {
  activePosition: 10 | 20 | 30;
  conclusionsPro: any;
  userScenarioResult: string;
  proCalculation: any;
}) => {
  const { cashFlowConclusions } = conclusionsPro || {};

  const sonderAfa = proCalculation?.baseCalculation?.isSonderAfaEnabled;
  const degressiveAfa = proCalculation?.baseCalculation?.isDegressiveAfaEnabled;
  const eth40 = proCalculation?.baseCalculation?.isKfw297Limit100Enabled;
  const eth40Qng = proCalculation?.baseCalculation?.isKfw297Limit150Enabled;

  if (userScenarioResult === CALCULATOR_SCENARIO_VARIANT.POSITIVE) {
    const derArticle = sonderAfa || degressiveAfa || eth40Qng;

    const investorCashFlowAveragePositive = Math.round(
      cashFlowConclusions.investorCashFlowAveragePositive?.[activePosition]
    );
    const investorCashFlowAverageNegative = Math.round(
      cashFlowConclusions.investorCashFlowAverageNegative?.[activePosition]
    );
    const investorCashFlowAverageDifferenceNegative =
      investorCashFlowAveragePositive - investorCashFlowAverageNegative;

    return {
      price: convertValueToLocale(investorCashFlowAveragePositive),
      caption: `${convertValueToLocale(
        investorCashFlowAverageNegative
      )} ohne Fördermaßnahmen`,
      description: `Dein zusätzlicher Vorteil durch Förderung +${convertValueToLocale(
        investorCashFlowAverageDifferenceNegative
      )} mtl.`,
      details: `Perfekt! Max. mögl. Vorteil`,
      chartData: [
        {
          chartItem: convertValueToLocale(investorCashFlowAveragePositive),
          value: Math.abs(investorCashFlowAveragePositive),
          valueColor: "#469c49",
        },
        {
          chartItem: convertValueToLocale(investorCashFlowAverageNegative),
          value: Math.abs(investorCashFlowAverageNegative),
          valueColor: "#DADDEA",
        },
      ],
      info: {
        section1: `Dank 
                  ${derArticle ? `der ` : ""}
                  ${sonderAfa ? `Sonder ` : ""}
                  ${sonderAfa ? `und ` : ""}
                  ${degressiveAfa ? `degressiven ` : ""}
                  ${degressiveAfa || sonderAfa ? `AfA` : ""} 
                  ${degressiveAfa || sonderAfa ? `und ` : ""}
                  ${
                    eth40Qng
                      ? `des KfW-geförderten Kredites nach 298 für enegrieeffizientes (EH40) und nachhaltiges (QNG) Bauen`
                      : ""
                  } 
                  beträgt Deine durchschnittliche monatliche Belastung in den ersten 
                  ${activePosition}
                  Jahren
                  ${convertValueToLocale(
                    Math.abs(investorCashFlowAveragePositive)
                  )}. Das sind 
                    ${convertValueToLocale(
                      investorCashFlowAverageDifferenceNegative
                    )}
                   weniger im Vergleich zu derselben Wohnung ohne diese Fördermaßnahmen.
                  Hier läge die Belastug bei
                  ${convertValueToLocale(
                    Math.abs(investorCashFlowAverageNegative)
                  )}.
                  `,
        section2: null,
      },
    };
  }

  if (userScenarioResult === "neutral") {
    const derArticle = sonderAfa || degressiveAfa || eth40Qng || eth40;

    const investorCashFlowAveragePositive = Math.round(
      cashFlowConclusions.investorCashFlowAveragePositive?.[activePosition]
    );
    const investorCashFlowAverageNeutral = Math.round(
      cashFlowConclusions.investorCashFlowAverageNeutral?.[activePosition]
    );
    const investorCashFlowAverageNegative = Math.round(
      cashFlowConclusions.investorCashFlowAverageNegative?.[activePosition]
    );
    const investorCashFlowAverageDifferenceNegative =
      investorCashFlowAverageNeutral - investorCashFlowAverageNegative;
    const investorCashFlowAverageDifferencePositive =
      investorCashFlowAveragePositive - investorCashFlowAverageNeutral;

    return {
      price: convertValueToLocale(investorCashFlowAverageNeutral),
      caption: `${convertValueToLocale(
        investorCashFlowAverageNegative
      )} ohne Fördermaßnahmen`,
      description: `Dein zusätzlicher Vorteil durch Förderung +${convertValueToLocale(
        investorCashFlowAverageDifferenceNegative
      )} mtl.`,
      details: `${convertValueToLocale(
        investorCashFlowAverageDifferencePositive
      )} weniger als max. mögl. Vorteil`,
      chartData: [
        {
          chartItem: convertValueToLocale(investorCashFlowAverageNeutral),
          value: Math.abs(investorCashFlowAverageNeutral),
          valueColor: "#ffbe42",
        },
        {
          chartItem: convertValueToLocale(investorCashFlowAverageNegative),
          value: Math.abs(investorCashFlowAverageNegative),
          valueColor: "#DADDEA",
        },
      ],
      info: {
        section1: `Dank 
                  ${derArticle ? `der ` : ""}
                  ${sonderAfa ? `Sonder ` : ""}
                  ${sonderAfa ? `und ` : ""}
                  ${degressiveAfa ? `degressiven ` : ""}
                  ${degressiveAfa || sonderAfa ? `und ` : ""}
                  ${
                    (degressiveAfa || sonderAfa) && eth40
                      ? `des KfW-geförderten Kredites nach 298 für enegrieeffizientes (EH40)`
                      : ""
                  }
                  ${
                    (degressiveAfa || sonderAfa) && eth40Qng
                      ? `des KfW-geförderten Kredites nach 298 für enegrieeffizientes (EH40) und nachhaltiges (QNG) Bauen`
                      : ""
                  }
                  beträgt Deine durchschnittliche monatliche Belastung in den ersten 
                  ${activePosition} 
                  Jahren 
                  ${convertValueToLocale(
                    Math.abs(investorCashFlowAverageNeutral)
                  )}
                  im Vergleich zu 
                   ${convertValueToLocale(
                     Math.abs(investorCashFlowAverageNegative)
                   )}
                  bei derselben Wohnung ohne diese Fördermaßnahmen.`,
        section2: `Das sind jedoch 
                  ${convertValueToLocale(
                    investorCashFlowAverageDifferencePositive
                  )}
                  mehr im Vergleich zur derselben Wohnung mit 
                  ${!sonderAfa ? `Sonder` : ""}
                  ${!degressiveAfa && !sonderAfa ? `und` : ""}
                  ${!degressiveAfa ? `degressiven` : ""}
                  ${!degressiveAfa || !sonderAfa ? `AfA` : ""}
                  ${(!degressiveAfa || !sonderAfa) && !eth40Qng ? `und` : ""}
                  ${
                    !eth40Qng
                      ? `dem KfW-geförderten Kredit nach 298 für enegrieeffizienten (EH40) und nachhaltigen (QNG) Bauen`
                      : ""
                  }.
                  Hier läge die Belastug bei nur
                  ${convertValueToLocale(
                    Math.abs(investorCashFlowAveragePositive)
                  )}.`,
      },
    };
  }

  const investorCashFlowAveragePositive = Math.round(
    cashFlowConclusions.investorCashFlowAveragePositive?.[activePosition]
  );
  const investorCashFlowAverageNegative = Math.round(
    cashFlowConclusions.investorCashFlowAverageNegative?.[activePosition]
  );
  const investorCashFlowAverageDifferencePositive =
    investorCashFlowAveragePositive - investorCashFlowAverageNegative;

  return {
    price: convertValueToLocale(investorCashFlowAverageNegative),
    caption: `${convertValueToLocale(
      investorCashFlowAveragePositive
    )} mit Fördermaßnahmen`,
    description: `${convertValueToLocale(
      investorCashFlowAverageDifferencePositive
    )} weniger als max. mögl. Vorteil`,
    details: null,
    chartData: [
      {
        chartItem: convertValueToLocale(investorCashFlowAverageNegative),
        value: Math.abs(investorCashFlowAverageNegative),
        valueColor: "#f85608",
      },
      {
        chartItem: convertValueToLocale(investorCashFlowAveragePositive),
        value: Math.abs(investorCashFlowAveragePositive),
        valueColor: "#DADDEA",
      },
    ],
    info: {
      section1: `Bei dieser Wohnung beträgt Deine durchschnittliche monatliche Belastung in den ersten 
                ${activePosition} 
                Jahren 
                ${convertValueToLocale(
                  Math.abs(investorCashFlowAverageNegative)
                )}
                im Vergleich zu
                ${convertValueToLocale(
                  Math.abs(investorCashFlowAveragePositive)
                )}
                bei derselben Wohnung mit der Sonder
                ${!degressiveAfa ? `und degressiven` : ""}
                AfA und des KfW-geförderten Kredites nach 298 für enegrieeffizientes (EH40) und nachhaltiges (QNG) Bauen.
                Du verlierst also in den ersten 
                ${activePosition} 
                Jahren durchschnittlich monatlich  
                ${convertValueToLocale(
                  Math.abs(investorCashFlowAverageDifferencePositive)
                )}.`,
      section2: null,
    },
  };
};

export const getPositiveCashFlowConclusion = ({
  conclusionsPro,
  userScenarioResult,
}: {
  conclusionsPro: any;
  userScenarioResult: string;
}) => {
  const { cashFlowConclusions } = conclusionsPro || {};

  if (
    userScenarioResult === CALCULATOR_SCENARIO_VARIANT.POSITIVE &&
    cashFlowConclusions.cashFlowDurationNeutral > 0
  ) {
    return `Mit Steuervorteilen hast du ${cashFlowConclusions.cashFlowDurationNeutral} Jahre lang
            einen positiven Cashflow.`;
  }

  if (
    userScenarioResult === "neutral" &&
    cashFlowConclusions.cashFlowDurationNeutral > 0
  ) {
    const equalCashFlowLength =
      cashFlowConclusions.cashFlowDurationNeutral ===
      cashFlowConclusions.cashFlowDurationPositive;

    if (equalCashFlowLength) {
      return `Mit Steuervorteilen hast du ${cashFlowConclusions.cashFlowDurationNeutral} Jahre lang
            einen positiven Cashflow.`;
    }

    return `Mit Steuervorteilen hast du ${cashFlowConclusions.cashFlowDurationNeutral} Jahre lang
            einen positiven Cashflow. Mit QNG hättest du ${cashFlowConclusions.cashFlowDurationPositive} Jahre lang
            einen positiven Cashflow.`;
  }

  return `Mit Fördermaßnahmen hättest du ${cashFlowConclusions.cashFlowDurationPositive} Jahre lang
            einen positiven Cashflow.`;
};

export const getTaxSavingsConclusion = ({
  activePosition,
  conclusionsPro,
  userScenarioResult,
  proCalculation,
}: {
  activePosition: 10 | 20 | 30;
  conclusionsPro: any;
  userScenarioResult: string;
  proCalculation: any;
}) => {
  const { taxSavingsAndProfitConclusions } = conclusionsPro || {};

  const sonderAfa = proCalculation?.baseCalculation?.isSonderAfaEnabled;
  const degressiveAfa = proCalculation?.baseCalculation?.isDegressiveAfaEnabled;

  if (userScenarioResult === CALCULATOR_SCENARIO_VARIANT.POSITIVE) {
    const totalTaxSavingsPositive = Math.round(
      taxSavingsAndProfitConclusions.totalTaxSavingsPositive?.[activePosition]
    );
    const totalTaxSavingsNegative = Math.round(
      taxSavingsAndProfitConclusions.totalTaxSavingsNegative?.[activePosition]
    );
    const totalTaxSavingsDifferenceNegative =
      totalTaxSavingsPositive - totalTaxSavingsNegative;

    return {
      price: convertValueToLocale(totalTaxSavingsPositive),
      caption: `${convertValueToLocale(
        totalTaxSavingsNegative
      )} ohne Fördermaßnahmen`,
      description: `${convertValueToLocale(
        totalTaxSavingsDifferenceNegative
      )} weniger als max. mögl. Vorteil`,
      details: `Perfekt! Max. mögl. Vorteil`,
      chartData: [
        {
          id: uuid(),
          chartItem: convertValueToLocale(totalTaxSavingsPositive),
          value: Math.abs(totalTaxSavingsPositive),
          valueColor: "#469c49",
        },
        {
          id: uuid(),
          chartItem: convertValueToLocale(totalTaxSavingsNegative),
          value: Math.abs(totalTaxSavingsNegative),
          valueColor: "#DADDEA",
        },
      ],
      info: {
        section1: `Bei dieser Wohnung verlierst Du Steuerersparnisse in Höhe von
                   ${convertValueToLocale(totalTaxSavingsDifferenceNegative)}
                   im Vergleich zu derselben Wohnung mit der zusätzlichen Fördermöglichkeit durch die degressive und die Sonder-AfA.`,
        section2: null,
      },
    };
  }

  if (userScenarioResult === "neutral") {
    const totalTaxSavingsPositive = Math.round(
      taxSavingsAndProfitConclusions.totalTaxSavingsPositive?.[activePosition]
    );
    const totalTaxSavingsNeutral = Math.round(
      taxSavingsAndProfitConclusions.totalTaxSavingsNeutral?.[activePosition]
    );
    const totalTaxSavingsNegative = Math.round(
      taxSavingsAndProfitConclusions.totalTaxSavingsNegative?.[activePosition]
    );
    const totalTaxSavingsDifferenceNegative =
      totalTaxSavingsNeutral - totalTaxSavingsNegative;
    const totalTaxSavingsDifferencePositive =
      totalTaxSavingsPositive - totalTaxSavingsNeutral;

    return {
      price: convertValueToLocale(totalTaxSavingsNeutral),
      caption: `${convertValueToLocale(
        totalTaxSavingsNegative
      )} ohne Fördermaßnahmen`,
      description: `${convertValueToLocale(
        totalTaxSavingsDifferenceNegative
      )} weniger als max. mögl. Vorteil`,
      details: `${convertValueToLocale(
        totalTaxSavingsDifferencePositive
      )} weniger als max. mögl. Vorteil`,
      chartData: [
        {
          id: uuid(),
          chartItem: convertValueToLocale(totalTaxSavingsNeutral),
          value: Math.abs(totalTaxSavingsNeutral),
          valueColor: "#ffbe42",
        },
        {
          id: uuid(),
          chartItem: convertValueToLocale(totalTaxSavingsNegative),
          value: Math.abs(totalTaxSavingsNegative),
          valueColor: "#DADDEA",
        },
      ],
      info: {
        section1:
          (sonderAfa || degressiveAfa) &&
          `Die Steuerersparnis aus 
                   der 
                   ${sonderAfa ? `Sonder` : ""}
                   ${sonderAfa ? `und` : ""}
                   ${degressiveAfa ? `degressive` : ""}
                   ${
                     sonderAfa || degressiveAfa ? `AfA` : ""
                   }  beträgt in den ersten
                   10 
                   Jahren 
                   ${convertValueToLocale(totalTaxSavingsNeutral)}`,
        section2: `Das sind  
                  ${convertValueToLocale(totalTaxSavingsDifferencePositive)}
                  weniger im Vergleich zu derselben Wohnung mit der zusätzlichen Fördermöglichkeit durch die
                  ${!sonderAfa ? `Sonder` : ""}
                  ${!sonderAfa ? `und` : ""}
                  ${!degressiveAfa ? `degressive` : ""}
                  ${
                    !sonderAfa || !degressiveAfa ? `AfA` : ""
                  }. Hier läge die Steuerersparnis aus Gesamt-AfA bei 
                  ${convertValueToLocale(totalTaxSavingsPositive)}.`,
      },
    };
  }

  const totalTaxSavingsPositive = Math.round(
    taxSavingsAndProfitConclusions.totalTaxSavingsPositive?.[activePosition]
  );
  const totalTaxSavingsNegative = Math.round(
    taxSavingsAndProfitConclusions.totalTaxSavingsNegative?.[activePosition]
  );
  const totalTaxSavingsDifferenceNegative =
    totalTaxSavingsPositive - totalTaxSavingsNegative;

  return {
    price: convertValueToLocale(totalTaxSavingsNegative),
    caption: `${convertValueToLocale(
      totalTaxSavingsPositive
    )} mit Fördermaßnahmen`,
    description: `${convertValueToLocale(
      totalTaxSavingsDifferenceNegative
    )} weniger als max. mögl. Vorteil`,
    details: null,
    chartData: [
      {
        id: uuid(),
        chartItem: convertValueToLocale(totalTaxSavingsNegative),
        value: Math.abs(totalTaxSavingsNegative),
        valueColor: "#f85608",
      },
      {
        id: uuid(),
        chartItem: convertValueToLocale(totalTaxSavingsPositive),
        value: Math.abs(totalTaxSavingsPositive),
        valueColor: "#DADDEA",
      },
    ],
    info: {
      section1: `Bei dieser Wohnung verlierst Du Steuerersparnisse in Höhe von
                 ${convertValueToLocale(totalTaxSavingsDifferenceNegative)}
                 im Vergleich zu derselben Wohnung mit der zusätzlichen Fördermöglichkeit durch die degressive und die Sonder-AfA.`,
      section2: null,
    },
  };
};

// =================== Loans =====================

export const getLoanConclusionTitle = ({
  activePosition,
  userScenarioResult,
  conclusionsPro,
}: {
  activePosition: 10 | 20 | 30;
  userScenarioResult: string;
  conclusionsPro: any;
}) => {
  const { loanConclusions } = conclusionsPro || {};

  if (!loanConclusions) {
    return "";
  }

  if (userScenarioResult === CALCULATOR_SCENARIO_VARIANT.POSITIVE) {
    return `Dein Vorteil in den ersten ${activePosition} Jahren: ca. <span class=${
      styles.chartTitlePositive
    }>${convertValueToLocale(
      loanConclusions.loanInterestDifferenceNegative[activePosition]
    )}</span>.`;
  }

  if (userScenarioResult === CALCULATOR_SCENARIO_VARIANT.NEUTRAL) {
    return `Dein Vorteil: ca. <span class=${
      styles.chartTitleNeutral
    }>${convertValueToLocale(
      loanConclusions.loanInterestDifferenceNegative[activePosition]
    )}</span> Ersparnis. Dir entgehen aber ca. <span class=${
      styles.chartTitleNeutral
    }>${convertValueToLocale(
      loanConclusions.loanInterestDifferencePositive[activePosition]
    )}</span> im Vergleich zu QNG.`;
  }

  return `Dir entgeht der Vorteil von ca. <span class=${
    styles.chartTitleNegative
  }>${convertValueToLocale(
    loanConclusions.loanInterestDifferencePositive[activePosition]
  )}</span> in den ersten ${activePosition} Jahren im Vergleich zu QNG.`;
};

export const getLoanConclusionDescription = (userScenarioResult: string) => {
  if (userScenarioResult === CALCULATOR_SCENARIO_VARIANT.POSITIVE) {
    return `Weil Dein Objekt den höchsten Energiestandard (QNG) erfüllt, erhältst
     du den maximalen KfW-Förderkredit von 150.000 €. Die Kombination dieses
      günstigeren Kredits mit der Hauptfinanzierung senkt deine Zinskosten erheblich.`;
  }

  if (userScenarioResult === CALCULATOR_SCENARIO_VARIANT.NEUTRAL) {
    return `Dein Objekt erfüllt den KFN-Standard und sichert Dir so einen KfW-Förderkredit von 100.000 €.
     Die Kombination dieses günstigeren Kredits mit der Hauptfinanzierung führt zu deiner Ersparnis.`;
  }

  return `Die Finanzierung erfolgt zu normalen Marktkonditionen, da das Objekt keine KfW-Förderung
   erhält. Dadurch zahlst Du im Vergleich zu geförderten Objekten höhere Zinsen.`;
};

//TODO Не удалять, это старый вид графика для сравнения помесячно, он может пригодиться в будущем
// export const getMonthlyPaymentChartData = ({
//   activePosition,
//   conclusionsPro,
//   userScenarioResult,
// }: {
//   activePosition: 10 | 20 | 30;
//   conclusionsPro: any;
//   userScenarioResult: string;
// }) => {
//   const { loanConclusions } = conclusionsPro || {};

//   if (userScenarioResult === CALCULATOR_SCENARIO_VARIANT.POSITIVE) {
//     return {
//       montlyPayment: convertValueToLocale(
//         loanConclusions.loanMonthlyPaymentNegative[activePosition]
//       ),
//       description: `(statt ~${convertValueToLocale(
//         loanConclusions.loanMonthlyPaymentNegative[activePosition]
//       )} am Markt)`,
//       regularMontlyPayment: convertValueToLocale(
//         loanConclusions.loanMonthlyPaymentNegative[activePosition]
//       ),
//       chartData: [
//         {
//           chartItem: convertValueToLocale(
//             loanConclusions.loanMonthlyPaymentPositive[activePosition]
//           ),
//           value: loanConclusions.loanMonthlyPaymentPositive[activePosition],
//           valueColor: "#14a077",
//         },
//         {
//           chartItem: convertValueToLocale(
//             loanConclusions.loanMonthlyPaymentNegative[activePosition]
//           ),
//           value: loanConclusions.loanMonthlyPaymentNegative[activePosition],
//           valueColor: "#496cdf",
//         },
//       ],
//     };
//   }

//   if (userScenarioResult === CALCULATOR_SCENARIO_VARIANT.NEUTRAL) {
//     return {
//       montlyPayment: convertValueToLocale(
//         loanConclusions.loanMonthlyPaymentNeutral[activePosition]
//       ),
//       description: `(statt ~${convertValueToLocale(
//         loanConclusions.loanMonthlyPaymentNegative[activePosition]
//       )} am Markt; aber mit QNG hättest du ~${convertValueToLocale(
//         loanConclusions.loanMonthlyPaymentPositive[activePosition]
//       )} )`,
//       regularMontlyPayment: convertValueToLocale(
//         loanConclusions.loanMonthlyPaymentNegative[activePosition]
//       ),
//       specialMontlyPayment: convertValueToLocale(
//         loanConclusions.loanMonthlyPaymentPositive[activePosition]
//       ),
//       chartData: [
//         {
//           chartItem: convertValueToLocale(
//             loanConclusions.loanMonthlyPaymentNeutral[activePosition]
//           ),
//           value: loanConclusions.loanMonthlyPaymentNeutral[activePosition],
//           valueColor: "#14a077",
//         },
//         {
//           chartItem: convertValueToLocale(
//             loanConclusions.loanMonthlyPaymentNegative[activePosition]
//           ),
//           value: loanConclusions.loanMonthlyPaymentNegative[activePosition],
//           valueColor: "#496cdf",
//         },
//         {
//           chartItem: convertValueToLocale(
//             loanConclusions.loanMonthlyPaymentPositive[activePosition]
//           ),
//           value: loanConclusions.loanMonthlyPaymentPositive[activePosition],
//           valueColor: "#0DB6C9",
//         },
//       ],
//     };
//   }

//   return {
//     montlyPayment: convertValueToLocale(
//       loanConclusions.loanMonthlyPaymentPositive[activePosition]
//     ),
//     description: `mit QNG wären es ~${convertValueToLocale(
//       loanConclusions.loanMonthlyPaymentPositive[activePosition]
//     )}`,
//     specialMontlyPayment: convertValueToLocale(
//       loanConclusions.loanMonthlyPaymentPositive[activePosition]
//     ),
//     chartData: [
//       {
//         chartItem: convertValueToLocale(
//           loanConclusions.loanMonthlyPaymentNegative[activePosition]
//         ),
//         value: loanConclusions.loanMonthlyPaymentNegative[activePosition],
//         valueColor: "#14a077",
//       },
//       {
//         chartItem: convertValueToLocale(
//           loanConclusions.loanMonthlyPaymentPositive[activePosition]
//         ),
//         value: loanConclusions.loanMonthlyPaymentPositive[activePosition],
//         valueColor: "#496cdf",
//       },
//     ],
//   };
// };

export const getLoanProfitChartData = ({
  activePosition,
  conclusionsPro,
  userScenarioResult,
  proCalculation,
}: {
  activePosition: 10 | 20 | 30;
  conclusionsPro: any;
  userScenarioResult: string;
  proCalculation: any;
}) => {
  const { loanConclusions } = conclusionsPro || {};

  const eth40 = proCalculation?.baseCalculation?.isKfw297Limit100Enabled;
  const eth40Qng = proCalculation?.baseCalculation?.isKfw297Limit150Enabled;

  if (userScenarioResult === CALCULATOR_SCENARIO_VARIANT.POSITIVE) {
    return {
      price: `${convertValueToLocale(
        loanConclusions.loanInterestDifferenceNegative[activePosition]
      )}`,
      caption: null,
      description: `Perfekt! Max. mögl. Vorteil`,
      details: `mit Fördermaßnahmen nach EH40 mit QNG`,
      chartData: [
        {
          chartItem: `${convertValueToLocale(
            loanConclusions.loanInterestDifferenceNegative[activePosition]
          )}`,
          value: loanConclusions.loanInterestDifferenceNegative[activePosition],
          valueColor: "#469c49",
        },
        {
          chartItem: `${convertValueToLocale(0)}`,
          value: 100,
          valueColor: "#DADDEA",
        },
      ],
      info: {
        section1: `Die Wohnung wird nach EH40 energieeffizient und nach QNG nachhaltig gebaut.
                  Daher werden
                  ${convertValueToLocale(
                    loanConclusions.specialLoanAmountPositive
                  )}  
                  von der Darlehenssumme von KfW (298) mit einem günstigeren Zinssatz in Höhe von
                  ${convertValueToLocale(
                    loanConclusions.interestRateSpecialPositive[activePosition],
                    2,
                    "%"
                  )} gefördert. 
                  Dies entspricht einer Ersparnis in den ersten ${activePosition} Jahren in Höhe von 
                  ${convertValueToLocale(
                    loanConclusions.loanInterestDifferenceNegative[
                      activePosition
                    ]
                  )}`,
        section2: null,
      },
    };
  }

  if (userScenarioResult === "neutral") {
    return {
      price: `${convertValueToLocale(
        loanConclusions.loanInterestDifferenceNegative[activePosition]
      )}`,
      caption: null,
      description:
        loanConclusions.loanInterestDifferencePositive[activePosition] > 0
          ? `${convertValueToLocale(
              loanConclusions.loanInterestDifferencePositive[activePosition]
            )} weniger als max. mögl. Vorteil`
          : `Perfekt! Max. mögl. Vorteil`,
      details: `mit Fördermaßnahmen nach EH40`,
      chartData: [
        {
          chartItem: `${convertValueToLocale(
            loanConclusions.loanInterestDifferenceNegative[activePosition]
          )}`,
          value: loanConclusions.loanInterestDifferenceNegative[activePosition],
          valueColor:
            loanConclusions.loanInterestDifferencePositive[activePosition] === 0
              ? "#469c49"
              : "#ffbe42",
        },
        {
          chartItem: `${convertValueToLocale(0)}`,
          value: 100,
          valueColor: "#DADDEA",
        },
      ],
      overwriteVariant:
        loanConclusions.loanInterestDifferencePositive[activePosition] === 0
          ? "positive"
          : null,
      info: {
        section1:
          (eth40 || eth40Qng) &&
          `Die Wohnung wird 
                      ${eth40 ? `nach EH40 energieeffizient` : ""} 
                      ${
                        eth40Qng
                          ? `nach EH40 energieeffizient und nach QNG nachhaltig`
                          : ""
                      } 
                      gebaut. Daher werden 
                      ${convertValueToLocale(
                        loanConclusions.specialLoanAmountNeutral
                      )} 
                      von der Darlehenssumme von KfW (298) mit einem günstigeren Zinssatz in Höhe von 
                      ${convertValueToLocale(
                        loanConclusions.interestRateSpecialNeutral[
                          activePosition
                        ],
                        2,
                        "%"
                      )} gefördert. Dies entspricht einer Ersparnis in den ersten ${activePosition} Jahren in Höhe von 
                      ${convertValueToLocale(
                        loanConclusions.loanInterestDifferenceNegative[
                          activePosition
                        ]
                      )}`,
        section2:
          !eth40Qng &&
          `Bei derselben Wohnung mit nach EH40 energieeffizient und nach QNG nachhaltig
                      wäre der geförderte Kreditbetrag bei 150.000 EUR von der Gesamtdarlehenssumme. Dadurch würde die Erparnis um
                      ${convertValueToLocale(
                        loanConclusions.loanInterestDifferenceNegative[
                          activePosition
                        ]
                      )}
                      auf
                      ${convertValueToLocale(
                        loanConclusions.loanInterestDifferenceNegative[
                          activePosition
                        ] +
                          loanConclusions.loanInterestDifferencePositive[
                            activePosition
                          ]
                      )}
                       ansteigen. Sie verlieren durch die fehlende Zertifizierung für nachhaltiges (QNG) Bauen
                       ${convertValueToLocale(
                         loanConclusions.loanInterestDifferencePositive[
                           activePosition
                         ]
                       )} EUR in den ersten ${activePosition} Jahren.`,
      },
    };
  }

  return {
    price: `0 €`,
    caption: null,
    description: `${convertValueToLocale(
      loanConclusions.loanInterestDifferencePositive[activePosition]
    )} weniger als max. mögl. Vorteil`,
    details: `ohne Fördermaßnahmen nach EH40 mit QNG`,
    chartData: [
      {
        chartItem: `${convertValueToLocale(0)}`,
        value: 100,
        valueColor: "#f85608",
      },
      {
        chartItem: `${convertValueToLocale(
          loanConclusions.loanInterestDifferencePositive[activePosition]
        )}`,
        value: loanConclusions.loanInterestDifferencePositive[activePosition],
        valueColor: "#DADDEA",
      },
    ],
    info: {
      section1: `Die Wohnung wird nach dem marktüblichen Zinssatz finanziert. Sie verlieren dadurch
                 ${convertValueToLocale(
                   loanConclusions.loanInterestDifferencePositive[
                     activePosition
                   ]
                 )} 
                 im Vergleich zu derselben Wohnung, die enegrieeffizient (EH40) und nachhaltig (QNG) gebaut werden würde. In diesem Fall würden 150.000 EUR von der Darlehenssumme von KfW (298) mit einem günstigeren Zinssatz in Höhe von
                 2.96% gefördert. Dies entspricht einer Ersparnis in den ersten ${activePosition} Jahren in Höhe von
                 ${convertValueToLocale(
                   loanConclusions.loanInterestDifferencePositive[
                     activePosition
                   ]
                 )}.`,
      section2: null,
    },
  };
};

export const getLoanRateChartData = ({
  activePosition,
  conclusionsPro,
  userScenarioResult,
  proCalculation,
}: {
  activePosition: 10 | 20 | 30;
  conclusionsPro: any;
  userScenarioResult: string;
  proCalculation: any;
}) => {
  const { loanConclusions } = conclusionsPro || {};

  const eth40 = proCalculation?.baseCalculation?.isKfw297Limit100Enabled;
  const eth40Qng = proCalculation?.baseCalculation?.isKfw297Limit150Enabled;

  if (userScenarioResult === CALCULATOR_SCENARIO_VARIANT.POSITIVE) {
    const info10Years = `Die Wohnung wird
                        ${eth40 ? `nach EH40 energieeffizient` : ""}
                        ${
                          eth40Qng
                            ? `nach EH40 energieeffizient und nach QNG nachhaltig`
                            : ""
                        }
                        ${
                          !eth40Qng && !eth40
                            ? `nach dem marktüblichen Zinssatz`
                            : ""
                        }
                        gebaut. Daher werden
                        ${convertValueToLocale(
                          loanConclusions.specialLoanAmountPositive
                        )}
                        von der Darlehenssumme mit KfW-Förderprogramm 298 mit einem günstigeren Zinssatz in Höhe von
                        ${convertValueToLocale(
                          loanConclusions.interestRateSpecialPositive[
                            activePosition
                          ],
                          2,
                          "%"
                        )}
                        finanziert. Die restlichen 
                        ${convertValueToLocale(
                          loanConclusions.regularLoanAmountPositive
                        )}
                        werden mit dem marktüblichen Zinssatz von
                         ${convertValueToLocale(
                           loanConclusions.interestRateRegularPositive[
                             activePosition
                           ],
                           2,
                           "%"
                         )}
                        finanziert. Der Durchschnittswert des Finanzierungszinssatzes über beide Kreditteile beträgt über 10 Jahre
                        ${convertValueToLocale(
                          loanConclusions.interestRatePositive[activePosition],
                          2,
                          "%"
                        )}`;

    const info20Years = `Die Wohnung wird
                        ${eth40 ? `nach EH40 energieeffizient` : ""}
                        ${
                          eth40Qng
                            ? `nach EH40 energieeffizient und nach QNG nachhaltig`
                            : ""
                        }
                        ${
                          !eth40Qng && !eth40
                            ? `nach dem marktüblichen Zinssatz`
                            : ""
                        }
                        gebaut. Daher werden
                        ${convertValueToLocale(
                          loanConclusions.specialLoanAmountPositive
                        )}
                        Darlehenssumme mit KfW-Förderprogramm 298 in den ersten 10 Jahren mit einem günstigeren Zinssatz in Höhe von 
                        ${convertValueToLocale(
                          loanConclusions.interestRateSpecialPositive[
                            activePosition
                          ],
                          2,
                          "%"
                        )} finanziert.
                        Die restlichen 
                        ${convertValueToLocale(
                          loanConclusions.regularLoanAmountPositive
                        )}
                        werden mit dem marktüblichen Zinssatz von
                         ${convertValueToLocale(
                           loanConclusions.interestRateRegularPositive[
                             activePosition
                           ],
                           2,
                           "%"
                         )}
                        finanziert. 
                        Nach Ablauf von 10 Jahren wird der günstigere geförderte Zinssatz auf das marktübliche Niveau angepasst. 
                        Der Durchschnittswert des Finanzierungszinssatzes über beide Kreditteile beträgt über 20 Jahre 
                        ${convertValueToLocale(
                          loanConclusions.interestRatePositive[20],
                          2,
                          "%"
                        )}`;

    const info30Years = `Die Wohnung wird
                        ${eth40 ? `nach EH40 energieeffizient` : ""}
                        ${
                          eth40Qng
                            ? `nach EH40 energieeffizient und nach QNG nachhaltig`
                            : ""
                        }
                        ${
                          !eth40Qng && !eth40
                            ? `nach dem marktüblichen Zinssatz`
                            : ""
                        }
                        gebaut. Daher werden
                        ${convertValueToLocale(
                          loanConclusions.specialLoanAmountPositive
                        )}
                        Darlehenssumme mit KfW-Förderprogramm 298 in den ersten 10 Jahren mit einem günstigeren Zinssatz in Höhe von 
                        ${convertValueToLocale(
                          loanConclusions.interestRateSpecialPositive[
                            activePosition
                          ],
                          2,
                          "%"
                        )} finanziert.
                        Die restlichen 
                        ${convertValueToLocale(
                          loanConclusions.regularLoanAmountPositive
                        )}
                        werden mit dem marktüblichen Zinssatz von
                         ${convertValueToLocale(
                           loanConclusions.interestRateRegularPositive[
                             activePosition
                           ],
                           2,
                           "%"
                         )}
                        finanziert. 
                        Nach Ablauf von 10 Jahren wird der günstigere geförderte Zinssatz auf das marktübliche Niveau angepasst. 
                        Der Durchschnittswert des Finanzierungszinssatzes über beide Kreditteile beträgt über 30 Jahre 
                        ${convertValueToLocale(
                          loanConclusions.interestRatePositive[30],
                          2,
                          "%"
                        )}`;

    return {
      price: `∅${convertValueToLocale(
        loanConclusions.interestRatePositive[activePosition],
        2,
        "%"
      )}`,
      caption: null,
      description: null,
      details: `Hier werden ${convertValueToLocale(
        loanConclusions.specialLoanAmountPositive
      )} nach dem günstigeren KfW-Zinssatz in Höhe von ∅${convertValueToLocale(
        loanConclusions.interestRateSpecialPositive[activePosition],
        2,
        "%"
      )} finanziert.`,
      chartData: [
        {
          chartItem: `∅${convertValueToLocale(
            loanConclusions.interestRateRegularPositive[activePosition],
            2,
            "%"
          )}`,
          value:
            loanConclusions.interestRateRegularPositive[activePosition].toFixed(
              2
            ),
          loanAmount: convertValueToLocale(
            loanConclusions.regularLoanAmountPositive
          ),
          valueColor: "#1e1e1e",
        },
        {
          chartItem: `∅${convertValueToLocale(
            loanConclusions.interestRateSpecialPositive[activePosition],
            2,
            "%"
          )}`,
          value:
            loanConclusions.interestRateSpecialPositive[activePosition].toFixed(
              2
            ),
          loanAmount: convertValueToLocale(
            loanConclusions.specialLoanAmountPositive
          ),
          valueColor: "#469c49",
        },
      ],
      info: {
        section1:
          activePosition === 10
            ? info10Years
            : activePosition === 20
            ? info20Years
            : info30Years,
        section2: null,
      },
    };
  }

  if (userScenarioResult === "neutral") {
    const eth40 = proCalculation?.baseCalculation?.isKfw297Limit100Enabled;
    const eth40Qng = proCalculation?.baseCalculation?.isKfw297Limit150Enabled;

    const info10Years = `Die Wohnung wird
                        ${eth40 ? `nach EH40 energieeffizient` : ""}
                        ${
                          eth40Qng
                            ? `nach EH40 energieeffizient und nach QNG nachhaltig`
                            : ""
                        }
                        ${
                          !eth40Qng && !eth40
                            ? `nach dem marktüblichen Zinssatz`
                            : ""
                        }
                        gebaut. Daher werden
                        ${convertValueToLocale(
                          loanConclusions.specialLoanAmountNeutral
                        )}
                        von der Darlehenssumme mit KfW-Förderprogramm 298 mit einem günstigeren Zinssatz in Höhe von
                        ${convertValueToLocale(
                          loanConclusions.interestRateSpecialNeutral[
                            activePosition
                          ],
                          2,
                          "%"
                        )}
                        finanziert. Die restlichen 
                        ${convertValueToLocale(
                          loanConclusions.regularLoanAmountNeutral
                        )}
                        werden mit dem marktüblichen Zinssatz von
                         ${convertValueToLocale(
                           loanConclusions.interestRateRegularNeutral[
                             activePosition
                           ],
                           2,
                           "%"
                         )}
                        finanziert. Der Durchschnittswert des Finanzierungszinssatzes über beide Kreditteile beträgt über 10 Jahre
                        ${convertValueToLocale(
                          loanConclusions.interestRateNeutral[activePosition],
                          2,
                          "%"
                        )}`;

    const info20Years = `Die Wohnung wird
                        ${eth40 ? `nach EH40 energieeffizient` : ""}
                        ${
                          eth40Qng
                            ? `nach EH40 energieeffizient und nach QNG nachhaltig`
                            : ""
                        }
                        ${
                          !eth40Qng && !eth40
                            ? `nach dem marktüblichen Zinssatz`
                            : ""
                        }
                        gebaut. Daher werden
                        ${convertValueToLocale(
                          loanConclusions.specialLoanAmountNeutral
                        )}
                        Darlehenssumme mit KfW-Förderprogramm 298 in den ersten 10 Jahren mit einem günstigeren Zinssatz in Höhe von 
                        ${convertValueToLocale(
                          loanConclusions.interestRateSpecialNeutral[
                            activePosition
                          ],
                          2,
                          "%"
                        )} finanziert.
                        Die restlichen 
                        ${convertValueToLocale(
                          loanConclusions.regularLoanAmountNeutral
                        )}
                        werden mit dem marktüblichen Zinssatz von
                         ${convertValueToLocale(
                           loanConclusions.interestRateRegularNeutral[
                             activePosition
                           ],
                           2,
                           "%"
                         )}
                        finanziert. 
                        Nach Ablauf von 10 Jahren wird der günstigere geförderte Zinssatz auf das marktübliche Niveau angepasst. 
                        Der Durchschnittswert des Finanzierungszinssatzes über beide Kreditteile beträgt über 20 Jahre 
                        ${convertValueToLocale(
                          loanConclusions.interestRateNeutral[20],
                          2,
                          "%"
                        )}`;

    const info30Years = `Die Wohnung wird
                        ${eth40 ? `nach EH40 energieeffizient` : ""}
                        ${
                          eth40Qng
                            ? `nach EH40 energieeffizient und nach QNG nachhaltig`
                            : ""
                        }
                        ${
                          !eth40Qng && !eth40
                            ? `nach dem marktüblichen Zinssatz`
                            : ""
                        }
                        gebaut. Daher werden
                        ${convertValueToLocale(
                          loanConclusions.specialLoanAmountNeutral
                        )}
                        Darlehenssumme mit KfW-Förderprogramm 298 in den ersten 10 Jahren mit einem günstigeren Zinssatz in Höhe von 
                        ${convertValueToLocale(
                          loanConclusions.interestRateSpecialNeutral[
                            activePosition
                          ],
                          2,
                          "%"
                        )} finanziert.
                        Die restlichen 
                        ${convertValueToLocale(
                          loanConclusions.regularLoanAmountNeutral
                        )}
                        werden mit dem marktüblichen Zinssatz von
                         ${convertValueToLocale(
                           loanConclusions.interestRateRegularNeutral[
                             activePosition
                           ],
                           2,
                           "%"
                         )}
                        finanziert. 
                        Nach Ablauf von 10 Jahren wird der günstigere geförderte Zinssatz auf das marktübliche Niveau angepasst. 
                        Der Durchschnittswert des Finanzierungszinssatzes über beide Kreditteile beträgt über 30 Jahre 
                        ${convertValueToLocale(
                          loanConclusions.interestRateNeutral[30],
                          2,
                          "%"
                        )}`;

    return {
      price: `∅${convertValueToLocale(
        loanConclusions.interestRateNeutral[activePosition],
        2,
        "%"
      )}`,
      caption: null,
      description: null,
      details: `Hier werden ${convertValueToLocale(
        loanConclusions.specialLoanAmountNeutral
      )} nach dem günstigeren KfW-Zinssatz in Höhe von ∅${convertValueToLocale(
        loanConclusions.interestRateSpecialNeutral[activePosition],
        2,
        "%"
      )} finanziert.`,
      chartData: [
        {
          chartItem: `∅${convertValueToLocale(
            loanConclusions.interestRateRegularNeutral[activePosition],
            2,
            "%"
          )}`,
          value:
            loanConclusions.interestRateRegularNeutral[activePosition].toFixed(
              2
            ),
          loanAmount: convertValueToLocale(
            loanConclusions.regularLoanAmountNeutral
          ),
          valueColor: "#1e1e1e",
        },
        {
          chartItem: `∅${convertValueToLocale(
            loanConclusions.interestRateSpecialNeutral[activePosition],
            2,
            "%"
          )}`,
          value:
            loanConclusions.interestRateSpecialNeutral[activePosition].toFixed(
              2
            ),
          loanAmount: convertValueToLocale(
            loanConclusions.specialLoanAmountNeutral
          ),
          valueColor: "#469c49",
        },
      ],
      overwriteVariant:
        loanConclusions.loanInterestDifferencePositive[activePosition] === 0
          ? "positive"
          : null,
      info: {
        section1:
          activePosition === 10
            ? info10Years
            : activePosition === 20
            ? info20Years
            : info30Years,
        section2: null,
      },
    };
  }

  return {
    price: `∅${convertValueToLocale(
      loanConclusions.interestRateNegative[activePosition],
      2,
      "%"
    )}`,
    caption: null,
    description: `Es gibt keine Förderung. Du zahlst den marktüblichen Zinssatz.`,
    details: null,
    chartData: [
      {
        chartItem: `∅${convertValueToLocale(
          loanConclusions.interestRateNegative[activePosition],
          2,
          "%"
        )}`,
        value: loanConclusions.interestRateNegative[activePosition].toFixed(2),
        loanAmount: convertValueToLocale(
          loanConclusions.regularLoanAmountNegative
        ),
        valueColor: "#1e1e1e",
      },
      {
        chartItem: null,
        value: 0.1,
        loanAmount: null,
        valueColor: "#469c49",
      },
    ],
    info: {
      section1: `Die Wohnung wird nach dem marktüblichen Zinssatz finanziert.
                 Die KfW-Förderprogramme können bei dieser Investition leider nicht angewandt werden.`,
      section2: null,
    },
  };
};

export const getRentValuePerSqMeter = ({
  calculation,
}: {
  calculation: any;
}) => {
  return convertValueToLocale(
    calculation?.rentalYields?.investmentObjectRentalYield?.rentalYield * 100,
    2,
    "%"
  );
};

export const getRentValueCaption = ({
  t,
  calculation,
}: {
  t: TFunction;
  calculation: any;
}) => {
  const apartments =
    calculation?.baseCalculation?.apartmentsCalculation.length > 1
      ? t("calculator_pro_page.results.apartments")
      : t("calculator_pro_page.results.apartment");

  const parkingSpacesLength =
    calculation?.baseCalculation?.parkingsCalculation.length;

  if (parkingSpacesLength === 1) {
    return `(${apartments} + ${t(
      "calculator_pro_page.results.parking_space"
    )})`;
  }

  if (parkingSpacesLength > 1) {
    return `(${apartments} + ${t(
      "calculator_pro_page.results.parking_spaces"
    )})`;
  }

  return `(${apartments})`;
};

export const getEarlySaleProfitConclusion = ({
  proCalculation,
  activePosition,
  conclusionsPro,
}: {
  proCalculation: any;
  activePosition: 10 | 20 | 30;
  conclusionsPro: any;
}) => {
  const { investmetAnalysisConclusions, userScenarioResult } =
    conclusionsPro || {};

  if (!investmetAnalysisConclusions) {
    return "";
  }

  const sonderAfa = proCalculation?.baseCalculation?.isSonderAfaEnabled;
  const degressiveAfa = proCalculation?.baseCalculation?.isDegressiveAfaEnabled;
  const eth40 = proCalculation?.baseCalculation?.isKfw297Limit100Enabled;
  const eth40Qng = proCalculation?.baseCalculation?.isKfw297Limit150Enabled;

  if (userScenarioResult === CALCULATOR_SCENARIO_VARIANT.POSITIVE) {
    const derArticle = sonderAfa || degressiveAfa || eth40Qng;

    const earlySaleProfitPositive = Math.round(
      investmetAnalysisConclusions.earlySaleProfitPositive[activePosition]
    );
    const earlySaleProfitNegative = Math.round(
      investmetAnalysisConclusions.earlySaleProfitNegative[activePosition]
    );
    const earlySaleProfitDifferenceNegative =
      earlySaleProfitPositive - earlySaleProfitNegative;

    return {
      price: convertValueToLocale(earlySaleProfitPositive),
      caption: `${convertValueToLocale(
        earlySaleProfitNegative
      )} ohne Fördermaßnahmen`,
      description: `Dein zusätzlicher Vorteil durch Förderung +${convertValueToLocale(
        earlySaleProfitDifferenceNegative
      )}`,
      details: `Perfekt! Max. mögl. Vorteil`,
      chartData: [
        {
          chartItem: convertValueToLocale(earlySaleProfitPositive),
          value: earlySaleProfitPositive,
          valueColor: "#469c49",
        },
        {
          chartItem: convertValueToLocale(earlySaleProfitNegative),
          value: earlySaleProfitNegative,
          valueColor: "#DADDEA",
        },
      ],
      info: {
        section1: `Dank 
                  ${derArticle ? `der ` : ""}
                  ${sonderAfa ? `Sonder ` : ""}
                  ${sonderAfa ? `und ` : ""}
                  ${degressiveAfa ? `degressiven ` : ""}
                  ${degressiveAfa || sonderAfa ? `AfA` : ""} 
                  ${degressiveAfa || sonderAfa ? `und ` : ""}
                  ${
                    eth40Qng
                      ? `des KfW-geförderten Kredites nach 298 für enegrieeffizientes (EH40) und nachhaltiges (QNG) Bauen`
                      : ""
                  } 
                  bekommst Du aus staatlichen Fördermaßnahmen in den ersten 
                  ${activePosition}
                  Jahren den höchstmöglichen Vorteil 
                  von 
                  ${convertValueToLocale(earlySaleProfitDifferenceNegative)}
                  im Vergleich zu derselben Wohnung ohne diese Fördermaßnahmen.`,
        section2: null,
      },
    };
  }

  if (userScenarioResult === "neutral") {
    const derArticle = sonderAfa || degressiveAfa || eth40Qng || eth40;
    const earlySaleProfitPositive = Math.round(
      investmetAnalysisConclusions.earlySaleProfitPositive[activePosition]
    );
    const earlySaleProfitNegative = Math.round(
      investmetAnalysisConclusions.earlySaleProfitNegative[activePosition]
    );
    const earlySaleProfitNeutral = Math.round(
      investmetAnalysisConclusions.earlySaleProfitNeutral[activePosition]
    );
    const earlySaleProfitDifferenceNegative =
      earlySaleProfitNeutral - earlySaleProfitNegative;
    const earlySaleProfitDifferencePositive =
      earlySaleProfitPositive - earlySaleProfitNeutral;
    return {
      price: convertValueToLocale(earlySaleProfitNeutral),
      caption: `${convertValueToLocale(
        earlySaleProfitNegative
      )} ohne Fördermaßnahmen`,
      description: `Dein zusätzlicher Vorteil durch Förderung +${convertValueToLocale(
        earlySaleProfitDifferenceNegative
      )}`,
      details: `${convertValueToLocale(
        earlySaleProfitDifferencePositive
      )} weniger als max. mögl. Vorteil`,
      chartData: [
        {
          chartItem: convertValueToLocale(earlySaleProfitNeutral),
          value: earlySaleProfitNeutral,
          valueColor: "#ffbe42",
        },
        {
          chartItem: convertValueToLocale(earlySaleProfitNegative),
          value: earlySaleProfitNegative,
          valueColor: "#DADDEA",
        },
      ],
      info: {
        section1: `Dank 
                  ${derArticle ? `der ` : ""}
                  ${sonderAfa ? `Sonder ` : ""}
                  ${sonderAfa ? `und ` : ""}
                  ${degressiveAfa ? `degressiven ` : ""}
                  ${degressiveAfa || sonderAfa ? `und ` : ""}
                  ${
                    (degressiveAfa || sonderAfa) && eth40
                      ? `des KfW-geförderten Kredites nach 298 für enegrieeffizientes (EH40)`
                      : ""
                  }
                  ${
                    (degressiveAfa || sonderAfa) && eth40Qng
                      ? `des KfW-geförderten Kredites nach 298 für enegrieeffizientes (EH40) und nachhaltiges (QNG) Bauen`
                      : ""
                  }
                  bekommst Du aus staatlichen Fördermaßnahmen in den ersten 
                  ${activePosition} 
                  Jahren den höchstmöglichen Vorteil von 
                  ${convertValueToLocale(earlySaleProfitDifferenceNegative)} 
                  im Vergleich zu derselben Wohnung ohne diese Fördermaßnahmen. `,
        section2: `Das ist 
                  ${convertValueToLocale(earlySaleProfitDifferencePositive)}  
                  weniger im Vergleich zur derselben Wohnung mit 
                  der
                  ${!sonderAfa ? `Sonder` : ""}
                  ${!degressiveAfa && !sonderAfa ? `und` : ""}
                  ${!degressiveAfa ? `degressiven` : ""}
                  ${!degressiveAfa || !sonderAfa ? `AfA` : ""}
                  ${(!degressiveAfa || !sonderAfa) && !eth40Qng ? `und` : ""}
                  ${
                    !eth40Qng
                      ? `dem KfW-geförderten Kredit nach 298 für enegrieeffizienten (EH40) und nachhaltigen (QNG) Bauen`
                      : ""
                  }`,
      },
    };
  }

  const earlySaleProfitPositive = Math.round(
    investmetAnalysisConclusions.earlySaleProfitPositive[activePosition]
  );
  const earlySaleProfitNegative = Math.round(
    investmetAnalysisConclusions.earlySaleProfitNegative[activePosition]
  );
  const earlySaleProfitDifferencePositive =
    earlySaleProfitPositive - earlySaleProfitNegative;

  return {
    price: convertValueToLocale(earlySaleProfitNegative),
    caption: `${convertValueToLocale(
      earlySaleProfitPositive
    )} mit Fördermaßnahmen`,
    description: `${convertValueToLocale(
      earlySaleProfitDifferencePositive
    )} weniger als max. mögl. Vorteil`,
    details: null,
    chartData: [
      {
        chartItem: convertValueToLocale(earlySaleProfitNegative),
        value: earlySaleProfitNegative,
        valueColor: "#f85608",
      },
      {
        chartItem: convertValueToLocale(earlySaleProfitPositive),
        value: earlySaleProfitPositive,
        valueColor: "#DADDEA",
      },
    ],
    info: {
      section1: `Bei dieser Wohnung verlierst Du an staatlichen Fördermaßnahmen in den ersten 
                ${activePosition} 
                Jahren 
                ${convertValueToLocale(earlySaleProfitDifferencePositive)} 
                im Vergleich zu derselben Wohnung mit der Sonder
                ${!degressiveAfa ? `und degressiven` : ""}
                AfA und des KfW-geförderten Kredites nach 298 für enegrieeffizientes (EH40) und nachhaltiges (QNG) Bauen.`,
      section2: null,
    },
  };
};

export const getInvestmentAccConclusion = ({
  activePosition,
  conclusionsPro,
  proCalculation,
}: {
  activePosition: 10 | 20 | 30;
  conclusionsPro: any;
  proCalculation: any;
}) => {
  const { taxSavingsAndProfitConclusions, userScenarioResult } =
    conclusionsPro || {};

  if (!taxSavingsAndProfitConclusions) {
    return "";
  }

  const sonderAfa = proCalculation?.baseCalculation?.isSonderAfaEnabled;
  const degressiveAfa = proCalculation?.baseCalculation?.isDegressiveAfaEnabled;
  const eth40 = proCalculation?.baseCalculation?.isKfw297Limit100Enabled;
  const eth40Qng = proCalculation?.baseCalculation?.isKfw297Limit150Enabled;

  if (userScenarioResult === CALCULATOR_SCENARIO_VARIANT.POSITIVE) {
    const derArticle = sonderAfa || degressiveAfa || eth40Qng;

    const investmentAccPositive = Math.round(
      taxSavingsAndProfitConclusions.investmentAccPositive[activePosition]
    );
    const investmentAccNegative = Math.round(
      taxSavingsAndProfitConclusions.investmentAccNegative[activePosition]
    );
    const investmentAccDifferenceNegative =
      investmentAccNegative - investmentAccPositive;
    return {
      price: convertValueToLocale(investmentAccPositive),
      caption: `${convertValueToLocale(
        investmentAccNegative
      )} ohne Fördermaßnahmen`,
      description: `Dein zusätzlicher Vorteil durch Förderung +${convertValueToLocale(
        investmentAccDifferenceNegative
      )}`,
      details: `Perfekt! Max. mögl. Vorteil`,
      chartData: [
        {
          chartItem: convertValueToLocale(investmentAccPositive),
          value: investmentAccPositive,
          valueColor: "#469c49",
        },
        {
          chartItem: convertValueToLocale(investmentAccNegative),
          value: investmentAccNegative,
          valueColor: "#DADDEA",
        },
      ],
      info: {
        section1: `Dank 
                  ${derArticle ? `der ` : ""}
                  ${sonderAfa ? `Sonder ` : ""}
                  ${sonderAfa ? `und ` : ""}
                  ${degressiveAfa ? `degressiven ` : ""}
                  ${degressiveAfa || sonderAfa ? `AfA` : ""} 
                  ${degressiveAfa || sonderAfa ? `und ` : ""}
                  ${
                    eth40Qng
                      ? `des KfW-geförderten Kredites nach 298 für enegrieeffizientes (EH40) und nachhaltiges (QNG) Bauen`
                      : ""
                  } 
                  investierst Du in den ersten 
                  ${activePosition}
                  Jahren den höchstmöglichen Vorteil 
                  von 
                  ${convertValueToLocale(investmentAccDifferenceNegative)}
                  weniger im Vergleich zu derselben Wohnung ohne diese Fördermaßnahmen.`,
        section2: null,
      },
    };
  }

  if (userScenarioResult === "neutral") {
    const derArticle = sonderAfa || degressiveAfa || eth40Qng || eth40;
    const investmentAccNeutral = Math.round(
      taxSavingsAndProfitConclusions.investmentAccNeutral[activePosition]
    );
    const investmentAccPositive = Math.round(
      taxSavingsAndProfitConclusions.investmentAccPositive[activePosition]
    );

    const investmentAccNegative = Math.round(
      taxSavingsAndProfitConclusions.investmentAccNegative[activePosition]
    );
    const investmentAccDifferenceNegative =
      investmentAccNegative - investmentAccNeutral;
    const investmentAccDifferencePositive =
      investmentAccNeutral - investmentAccPositive;
    return {
      price: convertValueToLocale(investmentAccNeutral),
      caption: `${convertValueToLocale(
        investmentAccNegative
      )} ohne Fördermaßnahmen`,
      description: `Dein zusätzlicher Vorteil durch Förderung +${convertValueToLocale(
        investmentAccDifferenceNegative
      )}`,
      details: `${convertValueToLocale(
        investmentAccDifferencePositive
      )} weniger als max. mögl. Vorteil`,
      chartData: [
        {
          chartItem: convertValueToLocale(investmentAccNeutral),
          value: investmentAccNeutral,
          valueColor: "#ffbe42",
        },
        {
          chartItem: convertValueToLocale(investmentAccNegative),
          value: investmentAccNegative,
          valueColor: "#DADDEA",
        },
      ],
      info: {
        section1: `Dank 
                  ${derArticle ? `der ` : ""}
                  ${sonderAfa ? `Sonder ` : ""}
                  ${sonderAfa ? `und ` : ""}
                  ${degressiveAfa ? `degressiven ` : ""}
                  ${degressiveAfa || sonderAfa ? `und ` : ""}
                  ${
                    (degressiveAfa || sonderAfa) && eth40
                      ? `des KfW-geförderten Kredites nach 298 für enegrieeffizientes (EH40)`
                      : ""
                  }
                  ${
                    (degressiveAfa || sonderAfa) && eth40Qng
                      ? `des KfW-geförderten Kredites nach 298 für enegrieeffizientes (EH40) und nachhaltiges (QNG) Bauen`
                      : ""
                  }
                  investierst Du  in den ersten 
                  ${activePosition} 
                  Jahren den höchstmöglichen Vorteil von 
                  ${convertValueToLocale(investmentAccNegative)} 
                  weniger im Vergleich zu derselben Wohnung ohne diese Fördermaßnahmen.`,
        section2: `Die Gesamtinvestition wäre um weitere 
                  ${convertValueToLocale(investmentAccDifferencePositive)}  
                  weniger im Vergleich zur derselben Wohnung mit der
                  ${!sonderAfa ? `Sonder` : ""}
                  ${!degressiveAfa && !sonderAfa ? `und` : ""}
                  ${!degressiveAfa ? `degressiven` : ""}
                  ${!degressiveAfa || !sonderAfa ? `AfA` : ""}
                  ${(!degressiveAfa || !sonderAfa) && !eth40Qng ? `und` : ""}
                  ${
                    !eth40Qng
                      ? `dem KfW-geförderten Kredit nach 298 für enegrieeffizienten (EH40) und nachhaltigen (QNG) Bauen`
                      : ""
                  }
                  Der Vorteil läge dann bei
                  ${convertValueToLocale(
                    investmentAccNegative - investmentAccPositive
                  )}`,
      },
    };
  }

  const investmentAccPositive = Math.round(
    taxSavingsAndProfitConclusions.investmentAccPositive[activePosition]
  );
  const investmentAccNegative = Math.round(
    taxSavingsAndProfitConclusions.investmentAccNegative[activePosition]
  );
  const investmentAccDifferenceNegative =
    investmentAccNegative - investmentAccPositive;

  return {
    price: convertValueToLocale(investmentAccNegative),
    caption: `${convertValueToLocale(
      investmentAccPositive
    )} mit Fördermaßnahmen`,
    description: `${convertValueToLocale(
      investmentAccDifferenceNegative
    )} weniger als max. mögl. Vorteil`,
    details: null,
    chartData: [
      {
        chartItem: convertValueToLocale(investmentAccNegative),
        value: investmentAccNegative,
        valueColor: "#f85608",
      },
      {
        chartItem: convertValueToLocale(investmentAccPositive),
        value: investmentAccPositive,
        valueColor: "#DADDEA",
      },
    ],
    info: {
      section1: `Bei dieser Wohnung verlierst Du an staatlichen Fördermaßnahmen in den ersten 
                ${activePosition} 
                Jahren 
                ${convertValueToLocale(investmentAccDifferenceNegative)} 
                im Vergleich zu derselben Wohnung mit der Sonder
                ${!degressiveAfa ? `und degressiven` : ""}
                AfA und des KfW-geförderten Kredites nach 298 für enegrieeffizientes (EH40) und nachhaltiges (QNG) Bauen.`,
      section2: null,
    },
  };
};
